import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { track } from "@/lib/analytics";

const CartContext = createContext(null);

function recomputeCart(items) {
  // Keep cart subtotal + count in sync with line items so the header chip and
  // cart-page total update INSTANTLY after an optimistic edit (no UI lag while
  // we wait for the server to respond — critical on mobile).
  const subtotal = items.reduce((s, it) => s + (it.unit_price || 0) * (it.quantity || 0), 0);
  const count = items.reduce((s, it) => s + (it.quantity || 0), 0);
  return { items, subtotal, count };
}

export function CartProvider({ children }) {
  const { user } = useAuth();
  const [cart, setCart] = useState({ items: [], subtotal: 0, count: 0 });
  const [wishlist, setWishlist] = useState({ product_ids: [], products: [], pairs: [] });

  const refresh = useCallback(async () => {
    if (!user) {
      setCart({ items: [], subtotal: 0, count: 0 });
      setWishlist({ product_ids: [], products: [], pairs: [] });
      return;
    }
    try {
      const [c, w] = await Promise.all([
        api.get("/cart"),
        api.get("/wishlist"),
      ]);
      setCart(c.data);
      setWishlist(w.data);
    } catch (e) {
      // ignore
    }
  }, [user]);

  useEffect(() => { refresh(); }, [refresh]);

  const addToCart = async (payload) => {
    await api.post("/cart/add", payload);
    await refresh();
    // After server confirms + cart re-fetched, find the matching line so we can
    // emit a value-rich add_to_cart event (GA4 + Meta Pixel).
    try {
      const { data: fresh } = await api.get("/cart");
      const line = (fresh.items || []).find(
        (it) => it.product_id === payload.product_id && it.size === payload.size && it.color === payload.color
      );
      const qty = payload.quantity || 1;
      track("add_to_cart", {
        value: (line?.unit_price || 0) * qty,
        items: [{
          item_id: payload.product_id,
          item_name: line?.product_name || line?.name,
          price: line?.unit_price || 0,
          quantity: qty,
        }],
      });
    } catch (_e) { /* analytics is best-effort */ }
  };

  // ---- Optimistic update: edit local state IMMEDIATELY, sync to server in BG ----
  const updateItem = async (id, quantity) => {
    const before = cart;
    const next = quantity <= 0
      ? recomputeCart(cart.items.filter((it) => it.id !== id))
      : recomputeCart(cart.items.map((it) => (it.id === id ? { ...it, quantity } : it)));
    setCart(next);
    try {
      await api.put(`/cart/items/${id}`, { quantity });
      // Background sync (don't block UI) — keeps any server-side recalcs (extras, discounts) in sync.
      api.get("/cart").then((r) => setCart(r.data)).catch(() => {});
    } catch (e) {
      // Revert on failure
      setCart(before);
      throw e;
    }
  };

  const removeItem = async (id) => {
    const before = cart;
    // Optimistically drop the item AND — if it belongs to a couple pair —
    // its partner too, so the UI matches the backend cascade delete without
    // a round-trip flicker.
    const target = cart.items.find((it) => it.id === id);
    const pairId = target?.couple_pair_id || null;
    const nextItems = pairId
      ? cart.items.filter((it) => it.couple_pair_id !== pairId)
      : cart.items.filter((it) => it.id !== id);
    setCart(recomputeCart(nextItems));
    try {
      await api.delete(`/cart/items/${id}`);
      api.get("/cart").then((r) => setCart(r.data)).catch(() => {});
      return { removed_couple_pair: !!pairId };
    } catch (e) {
      setCart(before);
      throw e;
    }
  };

  const clearCart = async () => {
    const before = cart;
    setCart({ items: [], subtotal: 0, count: 0 });
    try {
      await api.delete("/cart");
    } catch (e) {
      setCart(before);
      throw e;
    }
  };

  const toggleWishlist = async (product_id) => {
    const before = wishlist;
    const has = wishlist.product_ids.includes(product_id);
    setWishlist({
      ...wishlist,
      product_ids: has
        ? wishlist.product_ids.filter((p) => p !== product_id)
        : [...wishlist.product_ids, product_id],
    });
    try {
      if (has) {
        await api.delete(`/wishlist/${product_id}`);
      } else {
        await api.post("/wishlist/add", { product_id });
        track("add_to_wishlist", { items: [{ item_id: product_id }] });
      }
      api.get("/wishlist").then((r) => setWishlist(r.data)).catch(() => {});
    } catch (e) {
      setWishlist(before);
      throw e;
    }
  };

  // Save (or remove) BOTH halves of a couple pair as a single wishlist set.
  // Records a `pair` metadata entry so the Wishlist page can render them as
  // one grouped card with a "Add couple set to cart" CTA.
  const toggleWishlistPair = async (product_id_a, product_id_b) => {
    const existingPair = (wishlist.pairs || []).find((pr) => {
      const ids = (pr.product_ids || []).slice().sort();
      const wanted = [product_id_a, product_id_b].slice().sort();
      return ids[0] === wanted[0] && ids[1] === wanted[1];
    });
    try {
      if (existingPair) {
        await api.delete(`/wishlist/pair/${existingPair.id}`);
      } else {
        await api.post("/wishlist/add-pair", {
          product_id_a,
          product_id_b,
        });
        track("add_to_wishlist", { items: [{ item_id: product_id_a }, { item_id: product_id_b }] });
      }
      const { data } = await api.get("/wishlist");
      setWishlist(data);
      return { saved: !existingPair };
    } catch (e) {
      throw e;
    }
  };

  return (
    <CartContext.Provider value={{ cart, wishlist, refresh, addToCart, updateItem, removeItem, clearCart, toggleWishlist, toggleWishlistPair }}>
      {children}
    </CartContext.Provider>
  );
}

export const useCart = () => useContext(CartContext);
